import * as React from 'react'
import { Group, Panel, Separator } from 'react-resizable-panels'
import { DotsSixVertical } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'

/**
 * Resizable — drag-to-resize split panels (ResizablePanelGroup / ResizablePanel
 * / ResizableHandle), adopted from the TailGrids resizable model and built on
 * `react-resizable-panels`, wired to First Bite tokens.
 *
 * Note: `react-resizable-panels` v4 renamed its primitives (`Group` / `Panel` /
 * `Separator`, with an `orientation` prop) from the v2/v3 API the TailGrids spec
 * documents (`PanelGroup` / `PanelResizeHandle`, `direction`). These wrappers
 * keep the familiar shadcn-style names and forward to the v4 primitives.
 *
 *   ResizablePanelGroup  orientation horizontal | vertical
 *   ResizablePanel       defaultSize / minSize / maxSize / collapsible …
 *   ResizableHandle      withHandle shows a visible grip; disabled locks it
 *
 * Keyboard accessible out of the box: handles are focusable `role="separator"`s
 * resized with arrow keys (Home/End jump to min/max).
 */

type Orientation = 'horizontal' | 'vertical'

const OrientationContext = React.createContext<Orientation>('horizontal')

export type ResizablePanelGroupProps = React.ComponentProps<typeof Group> & {
  /**
   * Alias for `orientation`. react-resizable-panels v4 renamed `direction` →
   * `orientation`; this alias keeps v2/v3 + shadcn/TailGrids examples working.
   * If both are set, `orientation` wins.
   */
  direction?: Orientation
}

export const ResizablePanelGroup = ({
  orientation,
  direction,
  className,
  children,
  ...props
}: ResizablePanelGroupProps) => {
  const resolved: Orientation = orientation ?? direction ?? 'horizontal'
  return (
    <OrientationContext.Provider value={resolved}>
      <Group
        orientation={resolved}
        className={cn('h-full w-full', className)}
        {...props}
      >
        {children}
      </Group>
    </OrientationContext.Provider>
  )
}
ResizablePanelGroup.displayName = 'ResizablePanelGroup'

/** A single resizable panel. Re-exports the library `Panel` unchanged. */
export const ResizablePanel = Panel

export interface ResizableHandleProps extends React.ComponentProps<
  typeof Separator
> {
  /** Show a visible grip in the middle of the handle. */
  withHandle?: boolean
}

export const ResizableHandle = ({
  withHandle,
  className,
  ...props
}: ResizableHandleProps) => {
  const orientation = React.useContext(OrientationContext)
  const isVertical = orientation === 'vertical'
  return (
    <Separator
      className={cn(
        'relative flex items-center justify-center bg-fbui-gray-200 transition-colors hover:bg-fbui-basil-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fbui-ring-default data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        isVertical ? 'h-px w-full' : 'w-px h-full',
        className
      )}
      {...props}
    >
      {withHandle && (
        <div
          className={cn(
            'z-10 flex items-center justify-center rounded-sm border border-fbui-gray-300 bg-fbui-base-white text-fbui-gray-500',
            isVertical ? 'h-3 w-5 rotate-90' : 'h-5 w-3'
          )}
        >
          <DotsSixVertical
            weight="bold"
            className="size-3"
            aria-hidden="true"
          />
        </div>
      )}
    </Separator>
  )
}
ResizableHandle.displayName = 'ResizableHandle'
